import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import Slider from 'react-slick';
import {
  RiRocketLine,
  RiBrainLine,
  RiShieldKeyholeLine,
  RiRobot2Line
} from 'react-icons/ri';

import './HeroSection.css';
import HeroNetworkBackground from './HeroNetworkBackground';

import aiImage1 from '../assets/ai1.jpg';
import aiImage2 from '../assets/ai2.jpg';
import aiImage3 from '../assets/ai3.jpg';
import aiImage4 from '../assets/ai4.jpg';
import aiImage5 from '../assets/ai5.jpg';

const HeroSection = () => {
  const sliderRef = useRef(null);

  const sliderSettings = {
    dots: true,
    infinite: true,
    autoplay: true,
    autoplaySpeed: 4000,
    speed: 1500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    pauseOnDotsHover: true,
    pauseOnHover: true,
    fade: true,
    customPaging: i => (
      <div className="custom-dot">
        <div className="dot-pulse" />
      </div>
    ),
    appendDots: dots => (
      <div className="slider-dots-container">
        <ul className="slider-dots">{dots}</ul>
      </div>
    ),
    beforeChange: () => {
      document.querySelectorAll('.slide-content').forEach(el => {
        el.classList.remove('active');
      });
    },
    afterChange: current => {
      document.querySelectorAll('.slide-content')[current].classList.add('active');
    }
  };

  const slideContent = [
    {
      title: "AI-Powered Digital Transformation",
      description: "Leverage cutting-edge artificial intelligence to revolutionize your business processes",
      cta: "Explore AI Solutions",
      features: ["Predictive analytics engines", "Computer vision systems", "Natural language processing"]
    },
    {
      title: "Enterprise LLM Integration",
      description: "Deploy secure large language models with your proprietary knowledge base",
      cta: "Build Your AI Chatbot",
      features: ["RAG architecture", "Fine-tuned models", "API integrations"]
    },
    {
      title: "Intelligent Automation",
      description: "Combine RPA with AI decision engines for end-to-end process automation",
      cta: "Automate Workflows",
      features: ["Document processing", "Data extraction", "Workflow orchestration"]
    },
    {
      title: "AI-Driven Analytics",
      description: "Transform raw data into actionable insights with machine learning models",
      cta: "Get Data Insights",
      features: ["Real-time dashboards", "Anomaly detection", "Forecasting models"]
    },

    {
      title: "Cognitive Services at Scale",
      description: "Embed intelligent services across your enterprise with scalable APIs",
      cta: "Integrate AI APIs",
      features: ["Speech recognition", "Sentiment analysis", "Vision tagging"]
    }
  ];

  const features = [
    { icon: <RiRocketLine />, label: "Scalable AI architectures" },
    { icon: <RiBrainLine />, label: "Deep learning models" },
    { icon: <RiShieldKeyholeLine />, label: "Enterprise-grade security" },
    { icon: <RiRobot2Line />, label: "Autonomous agents" }
  ];

  return (
    <section className="hero" id="home">
      <div className="hero-ai-animated" />

      <HeroNetworkBackground />

      <div className="glam-effect" />
      <div className="hero-circuit" />
      <div className="hero-particles" />
      <div className="ai-connections" />

      <div className="hero-container">
        <div className="hero-text">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            NovaCrest AI & Software Solutions
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            We build intelligent systems that learn, adapt, and transform business operations through AI.
          </motion.p>

          <motion.ul
            className="hero-bullets"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {features.map((feature, index) => (
              <motion.li
                key={index}
                whileHover={{ x: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
                className="hero-feature-item"
              >
                <span className="icon">{feature.icon}</span>
                {feature.label}
              </motion.li>
            ))}
          </motion.ul>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <a href="/contact-us" className="hero-button">
              <span>Start AI Project</span>
              <div className="button-arrow">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </a>
          </motion.div>
        </div>

        <div className="hero-slider-wrapper">
          <Slider ref={sliderRef} {...sliderSettings}>
            {[aiImage1, aiImage2, aiImage3, aiImage4, aiImage5].map((image, index) => (
              <div key={index} className="slide-container">
                <div className="slide-image-wrapper">
                  <img
                    src={image}
                    alt={`AI Visual ${index + 1}`}
                    className="slide-image"
                  />
                  <div className="image-overlay" />
                </div>
                <div className={`slide-content ${index === 0 ? 'active' : ''}`}>
                  <h3>{slideContent[index].title}</h3>
                  <p>{slideContent[index].description}</p>
                  <ul>
                    {slideContent[index].features.map((feature, i) => (
                      <li key={i}>{feature}</li>
                    ))}
                  </ul>
                  <button
                    className="slide-cta"
                    onClick={() => console.log(`Navigating to ${slideContent[index].cta}`)}
                  >
                    {slideContent[index].cta}
                    <span className="cta-arrow">→</span>
                  </button>
                </div>
              </div>
            ))}
          </Slider>

          <div className="slider-controls">
            <button
              className="control-prev"
              onClick={() => sliderRef.current.slickPrev()}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              className="control-next"
              onClick={() => sliderRef.current.slickNext()}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
